import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import * as React from 'react';

import { useWorkItemStatusCounts } from '../queries/useWorkItemStatusCounts';
import { useFeatureGate } from '../useFeatureGates';

import type { IConversationMessage } from '../useAssistantWebSocket';

/**
 * The number the welcome header states, frozen at the first resolved read
 * after each welcome row appears. A greeting is an utterance, not a
 * dashboard: bound live, the line would rewrite itself as the user resolves
 * alerts and could disagree with a reply beneath it that already stated the
 * number. The source is the same cache entry the bell and the Notification
 * Center card read, so at freeze time it matches the card below.
 *
 * The freeze is keyed on the welcome ROW's identity, not the thread-reset
 * counter. A row's id is its thread's, so a reconnect to the same thread keeps
 * the frozen number, while every new thread — `/reset`, an inactivity
 * rollover's reconnect — re-freezes at its own number even when no
 * `thread_reset` frame fired. A page load starts unfrozen, so a reload states
 * the count at load. (A mid-session rebind's snapshot carries no welcome — it
 * drops the old row, and this hook returns `null` with it until the next
 * bind.)
 *
 * @param messages - the transcript; only the first `kind: 'welcome'` row's
 *   identity is read.
 * @returns the count frozen for the welcome row currently on screen, or
 *   `null` when no number can honestly be stated — no welcome row, the count
 *   still resolving, the read failed, or the alert surfaces gated off — in
 *   which case the header renders the greeting alone.
 */
export function useWelcomeAlertCount(messages: readonly IConversationMessage[]): number | null {
  const isAlertsCenterEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const { counts, loading, error } = useWorkItemStatusCounts({ skip: !isAlertsCenterEnabled });
  const [frozen, setFrozen] = React.useState<{ rowId: string; count: number } | null>(null);

  const welcomeRowId = messages.find((m) => m.kind === 'welcome')?.id ?? null;

  React.useEffect(() => {
    if (welcomeRowId === null || frozen?.rowId === welcomeRowId) {
      return;
    }
    if (!isAlertsCenterEnabled || loading || error !== undefined) {
      return;
    }
    setFrozen({ rowId: welcomeRowId, count: counts.active });
  }, [welcomeRowId, frozen, isAlertsCenterEnabled, loading, error, counts.active]);

  return frozen !== null && frozen.rowId === welcomeRowId ? frozen.count : null;
}
